import { monthKeyOf } from "./utils";

export { computeStatusCounts } from "../mappers/statusCounts.mapper";

// Cells for the currently selected month.
export const selectCurrentCells = (state) => state.byMonth[monthKeyOf(state.filters.selectedMonth)] || {};

// Cells belonging to one player (for the player-card status counts).
export const selectPlayerCells = (cells, playerId) =>
    Object.fromEntries(Object.entries(cells).filter(([key]) => key.startsWith(`${playerId}_`)));

// True if a cell carries at least one flag matching a selected flag-type filter value.
// Filter values: "gps" (over/under), "forceplate", "testing", "udf:<user_defined_flag_id>".
const cellMatchesFlagFilter = (cell, flagTypesFilter) => {
    const flags = cell?.flags;
    if (!flags || Array.isArray(flags) || Object.keys(flags).length === 0) return false;
    return flagTypesFilter.some((val) => {
        if (val === "gps") return !!(flags.gps_overworked || flags.gps_underworked);
        if (val === "forceplate") return !!flags.forceplate;
        if (val === "testing") return !!flags.testing;
        if (val.startsWith("udf:")) {
            const id = Number(val.slice(4));
            return Array.isArray(flags.user_defined) &&
                flags.user_defined.some((f) => f.user_defined_flag_id === id);
        }
        return false;
    });
};

// Copied from StatusTracker.isPlayerIncluded (monthUKeys passed in); + flag-type filter.
const isPlayerIncluded = (player, monthUKeys, filters, playerGroups, statuses, cells) => {
    const { playersFilter, positionsFilter, playerGroupsFilter, playerStatusesFilter, flagTypesFilter = [] } = filters;

    let selectPlayerGroupIds = [];
    if (playerGroupsFilter.length > 0) {
        selectPlayerGroupIds = playerGroups
            .filter((pg) => playerGroupsFilter.includes(pg.player_group_id))
            .map((pg) => pg.player_group_id);
    }

    let playersWithFilteredStatuses = new Set();
    if (playerStatusesFilter.length > 0) {
        const selectPlayerStatuses = statuses.filter((s) => playerStatusesFilter.includes(s.id));
        for (let uK in cells) {
            if (monthUKeys.has(uK)) {
                const [playerId] = uK.split("_");
                if (selectPlayerStatuses.some((ps) => ps.name === cells[uK]?.status?.name)) {
                    playersWithFilteredStatuses.add(Number(playerId));
                }
            }
        }
    }

    let playersWithFilteredFlags = new Set();
    if (flagTypesFilter.length > 0) {
        for (let uK in cells) {
            if (monthUKeys.has(uK) && cellMatchesFlagFilter(cells[uK], flagTypesFilter)) {
                const [playerId] = uK.split("_");
                playersWithFilteredFlags.add(Number(playerId));
            }
        }
    }

    if (
        (playersFilter.length > 0 && !playersFilter.includes(player?.player_id)) ||
        (positionsFilter.length > 0 && !positionsFilter.includes(player?.position)) ||
        // Match on player.player_groups: a group created in this session has no player_ids.
        (selectPlayerGroupIds.length > 0 &&
            !(player?.player_groups ?? []).some((id) => selectPlayerGroupIds.includes(id))) ||
        (playerStatusesFilter.length > 0 && !playersWithFilteredStatuses.has(player?.player_id)) ||
        (flagTypesFilter.length > 0 && !playersWithFilteredFlags.has(player?.player_id))
    ) {
        return false;
    }

    return true;
};

// Players that pass the active filters (unpaginated) — shared by the rows and the header count.
export const selectFilteredPlayers = (players, dateStrs, filters, playerGroups, statuses, cells) => {
    const monthUKeys = new Set();
    for (const dt of dateStrs) {
        for (const p of players) monthUKeys.add(`${p.player_id}_${dt}`);
    }
    return players.filter((p) => isPlayerIncluded(p, monthUKeys, filters, playerGroups, statuses, cells));
};

// Restrict a cell map to a set of player ids (for header counts over filtered rows).
export const filterCellsByPlayerIds = (cells, playerIdSet) =>
    Object.fromEntries(Object.entries(cells).filter(([key]) => playerIdSet.has(Number(key.split("_")[0]))));

// Full (unsliced) row set: [{ player, id, [dateStr]: uniqueKey }] — matches the v1 dataSource build.
export const buildFullRows = (players, dateStrs, filters, playerGroups, statuses, cells) =>
    selectFilteredPlayers(players, dateStrs, filters, playerGroups, statuses, cells).map((player) => {
        const row = { player, id: player?.player_id };
        for (const dt of dateStrs) row[String(dt)] = `${player?.player_id}_${dt}`;
        return row;
    });
